/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, useEffect, memo } from 'react';
import CatalogContext from './CatalogContext';
import initialCatalogState from './initialCatalogState';
import * as CatalogTypes from 'contexts/catalog/reducer/catalog.types';
import catalogReducer from 'contexts/catalog/reducer/catalog.reducer';
import * as action from 'contexts/catalog/reducer/catalog.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { initialProductsQuantity } from 'utils/constants';
import { useTranslation } from 'react-i18next';
import { abortCatalogController } from 'utils/abortController';
import useContextUser from 'hooks/contexts/useContextUser';
import {
  IaddLikeActionProps,
  IremoveLikeActionProps,
  IUpdateProductProps,
  TCatalogFormatView,
  TQuerySearchArray,
  TSortBy,
} from 'interfaces/catalog.interface';
import { IChildrenProps } from 'interfaces/general.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';

function CatalogProvider(props: IChildrenProps) {
  const [catalogState, dispatch] = useReducer(catalogReducer, initialCatalogState);
  const { getAccessTokenSilently } = useAuth0();
  const { dbUser } = useContextUser();
  const { company } = useContextCompany();
  const { t: translate } = useTranslation();

  useEffect(() => {
    if (!company?.brand?.id || !dbUser?.i18n_lang_id) return;
    dispatch({
      type: CatalogTypes.SET_BODY_PRODUCT,
      payload: {
        brandId: company.brand.id,
        limit: initialProductsQuantity,
        languageId: dbUser.i18n_lang_id,
      },
    });
  }, [company?.brand?.id, dbUser?.i18n_lang_id]);

  useEffect(() => {
    if (!company?.brand?.id) return;
    (async () => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.getProductAttributesAction({
        dispatch,
        brandId: company.brand.id,
        token,
      });
    })();
  }, [company?.brand?.id]);

  // Catalog
  const getCatalog = useCallback(async () => {
    const signal = await abortCatalogController();
    if (!catalogState.bodyProductSearch.options.brand_id[0] || !company?.brand.id)
      return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.getCatalogAction({
      dispatch,
      brandId: company.brand.id,
      bodyProductSearch: catalogState.bodyProductSearch,
      signal,
      translate,
      token,
    });
  }, [catalogState.bodyProductSearch, company?.brand?.id]);

  const addMoreProductsToCatalog = useCallback(async () => {
    const signal = await abortCatalogController();
    if (signal.aborted || !catalogState.bodyProductSearch) return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.addMoreProductsToCatalogAction({
      dispatch,
      bodyProductSearch: catalogState.bodyProductSearch,
      signal,
      translate,
      token,
    });
  }, [catalogState.bodyProductSearch]);

  const addLike = useCallback(
    async ({ productId }: IaddLikeActionProps) => {
      if (!dbUser?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.addLikeAction({
        dispatch,
        userId: dbUser.id,
        productId,
        translate,
        token,
      });
    },
    [dbUser?.id]
  );

  const removeLike = useCallback(
    async ({ productId, favorites }: IremoveLikeActionProps) => {
      if (!dbUser?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.removeLikeAction({
        dispatch,
        productId,
        favorites,
        userId: dbUser.id,
        translate,
        token,
      });
    },
    [dbUser?.id]
  );

  const resetCatalog = useCallback(() => {
    return dispatch({ type: CatalogTypes.RESET_CATALOG });
  }, []);

  // Filters
  const applyFilters = useCallback(async () => {
    const signal = await abortCatalogController();
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.applyFiltersAction({
      dispatch,
      catalogState,
      translate,
      signal,
      token,
    });
  }, [catalogState]);

  const addBodyFilters = useCallback(async (field: string, value: string) => {
    return await action.addBodyFiltersAction(dispatch, field, value);
  }, []);

  const removeBodyFilters = useCallback(async (field: string, value: string) => {
    return await action.removeBodyFiltersAction(dispatch, field, value);
  }, []);

  const resetBodyFilters = useCallback(
    async (setOpenDrawer?: any) => {
      if (!catalogState.bodyProductSearch.options.brand_id[0]) return;
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.resetBodyFiltersAction({
        dispatch,
        bodyProductSearch: catalogState.bodyProductSearch,
        setOpenDrawer,
        signal,
        translate,
        token,
      });
    },
    [catalogState.bodyProductSearch]
  );

  const setPriceSliders = useCallback(async (field: string, value: any) => {
    return await action.setPriceSlidersAction(dispatch, field, value);
  }, []);

  const setFavorites = useCallback(async (userId: string | null) => {
    return await action.setFavoritesAction(dispatch, userId);
  }, []);

  const resetPriceSlider = useCallback(
    async (type: string) => {
      if (!catalogState.bodyProductSearch.options.brand_id[0]) return;
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.resetPriceSliderAction({
        dispatch,
        type,
        bodyProductSearch: catalogState.bodyProductSearch,
        signal,
        translate,
        token,
      });
    },
    [catalogState.bodyProductSearch]
  );

  // Product
  const getProduct = useCallback(
    async ({ productId }: { productId: string }) => {
      if (!dbUser?.id || !dbUser.i18n_lang_id || !company?.brand?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.getProductAction({
        dispatch,
        productId,
        brandId: company.brand?.id,
        languageId: dbUser.i18n_lang_id,
        userId: dbUser.id,
        translate,
        token,
      });
    },
    [company?.brand?.id, dbUser?.id, dbUser?.i18n_lang_id]
  );

  const updateProduct = useCallback(
    async ({ fieldName, value, selectedValues }: IUpdateProductProps) => {
      if (!company?.brand?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateProductAction({
        dispatch,
        fieldName,
        value,
        selectedValues,
        token,
      });
    },
    [company?.brand?.id]
  );

  // Related Products
  const getRelatedProducts = useCallback(
    async ({ family }: { family: string }) => {
      if (!catalogState.bodyProductSearch.options.brand_id[0]) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.getRelatedProductsAction({
        dispatch,
        bodyProductSearch: catalogState.bodyProductSearch,
        family,
        token,
      });
    },
    [catalogState.bodyProductSearch]
  );

  // Search Engine
  const setQuerySearch = useCallback(async (query: string) => {
    return await action.setQuerySearchAction(dispatch, query);
  }, []);

  const addToBodySearch = useCallback(
    async (query: string) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.addToBodySearchAction(
        dispatch,
        query,
        catalogState,
        signal,
        translate,
        token
      );
    },
    [catalogState]
  );

  const removeFromBodySearch = useCallback(
    async (query: string) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.removeFromBodySearchAction(
        dispatch,
        query,
        catalogState,
        signal,
        translate,
        token
      );
    },
    [catalogState]
  );

  const addToQuerySearchArray = useCallback(async (value: TQuerySearchArray) => {
    return await action.addToQuerySearchArrayAction(dispatch, value);
  }, []);

  const removeFromQuerySearchArray = useCallback(async (value: TQuerySearchArray) => {
    return await action.removeFromQuerySearchArrayAction(dispatch, value);
  }, []);

  const removeAllFromBodySearch = useCallback(async () => {
    const signal = await abortCatalogController();
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.removeAllFromBodySearchAction(
      dispatch,
      catalogState,
      signal,
      translate,
      token
    );
  }, [catalogState]);

  const removeFromBodySearchFilter = useCallback(
    async (value: any) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.removeFromBodySearchFilterAction(
        dispatch,
        value,
        catalogState,
        signal,
        translate,
        token
      );
    },
    [catalogState]
  );

  // Sort By
  const setSortBy = useCallback(
    async (sortBy: TSortBy) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.setSortByAction(
        dispatch,
        sortBy,
        catalogState,
        signal,
        translate,
        token
      );
    },
    [catalogState]
  );

  const setSortByHaveImages = useCallback(
    async (haveImages: boolean | null) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.setSortByHaveImagesAction(
        dispatch,
        haveImages,
        catalogState.bodyProductSearch,
        signal,
        translate,
        token
      );
    },
    [catalogState.bodyProductSearch]
  );

  const setFormatView = useCallback((format: TCatalogFormatView) => {
    return dispatch({ type: CatalogTypes.SET_FORMAT_VIEW, payload: format });
  }, []);

  const memoProvider = useMemo(
    () => ({
      ...catalogState,
      getCatalog,
      addMoreProductsToCatalog,
      addToBodySearch,
      removeFromBodySearch,
      removeAllFromBodySearch,
      removeFromBodySearchFilter,
      getProduct,
      updateProduct,
      applyFilters,
      addBodyFilters,
      removeBodyFilters,
      resetBodyFilters,
      setQuerySearch,
      setSortBy,
      setPriceSliders,
      setFavorites,
      addLike,
      removeLike,
      getRelatedProducts,
      addToQuerySearchArray,
      removeFromQuerySearchArray,
      resetPriceSlider,
      resetCatalog,
      setSortByHaveImages,
      setFormatView,
    }),
    [
      catalogState,
      getCatalog,
      addMoreProductsToCatalog,
      addToBodySearch,
      removeFromBodySearch,
      removeAllFromBodySearch,
      removeFromBodySearchFilter,
      getProduct,
      updateProduct,
      applyFilters,
      addBodyFilters,
      removeBodyFilters,
      resetBodyFilters,
      setQuerySearch,
      setSortBy,
      setPriceSliders,
      setFavorites,
      addLike,
      removeLike,
      getRelatedProducts,
      addToQuerySearchArray,
      removeFromQuerySearchArray,
      resetPriceSlider,
      resetCatalog,
      setSortByHaveImages,
      setFormatView,
    ]
  );

  return (
    <CatalogContext.Provider value={memoProvider}>
      {props.children}
    </CatalogContext.Provider>
  );
}

export default memo(CatalogProvider);
